import { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  query,
  where,
  orderBy,
} from "firebase/firestore";

export default function VideoTrimming() {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [trimStartSec, setTrimStartSec] = useState<number>(0);
  const [trimEndSec, setTrimEndSec] = useState<number>(10);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [trimming, setTrimming] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [customRejectReason, setCustomRejectReason] = useState("");
  const [rejectingSubmission, setRejectingSubmission] = useState<any | null>(null);

  const [showQAModal, setShowQAModal] = useState(false);
  const [qaReason, setQaReason] = useState("");
  const [qaReviewingSubmission, setQaReviewingSubmission] = useState<any | null>(null);

  const rejectionReasons = [
    "Wrong item photographed",
    "Image quality too poor",
    "Doesn't meet task criteria",
    "Inappropriate content",
    "Duplicate submission",
    "Other"
  ];

  const qaReviewReasons = [
    "Not Sure if Task is Correct",
    "Image Quality Questionable",
    "Questionable Approval",
    "inappropriate",
    "Possible Duplicate",
    "Looks like AI",
    "Looks like Computer Screen"
  ];

  useEffect(() => {
    document.title = 'Video Trimming — Pixano';
  }, []);

  useEffect(() => {
    if (selected) {
      setTrimStartSec(0);
      setTrimEndSec(10);
      setVideoDuration(0);
      setTrimming(false);
    }
  }, [selected?.id]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "submissions"),
          where('status', 'in', ['pending', 'flagged', 'approved']),
          where('taskType', '==', 'Video'),
          where('videoStage', '==', 'trimming'),
          orderBy('createdAt', 'asc')
        );
        const submissionsSnap = await getDocs(q);

        if (submissionsSnap.empty) {
          setSubmissions([]);
          setSelected(null);
          setLoading(false);
          return;
        }

        const submissionsWithTask = await Promise.all(
          submissionsSnap.docs.map(async (sub) => {
            const subData = sub.data();
            const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
            return {
              id: sub.id,
              ...subData,
              task: taskSnap.exists() ? taskSnap.data() : null
            };
          })
        );

        setSubmissions(submissionsWithTask);
        setSelected(submissionsWithTask[0] || null);
      } catch (error) {
        console.error('Error fetching video trimming submissions:', error);
        setSubmissions([]);
        setSelected(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, []);

  const currentImageUrl = selected
    ? (selected.mediaUrl || selected.videoUrl || null)
    : null;

  const getStatusBadge = (status: string) => {
    if (status === "flagged") {
      return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">Flagged</span>;
    }
    return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Pending</span>;
  };

  const getSimilarityInfo = (submission: any) => {
    if (submission.validationDetails?.similarity) {
      return (
        <div className="mb-2">
          <span className="font-semibold">Similarity Score: </span>
          <span className={`${submission.validationDetails.similarity >= 0.6 ? 'text-green-600' : 'text-yellow-600'}`}>
            {(submission.validationDetails.similarity * 100).toFixed(1)}%
          </span>
        </div>
      );
    }
    return null;
  };

  const handleRejectClick = () => {
    setRejectingSubmission(selected);
    setShowRejectModal(true);
    setRejectReason("");
    setCustomRejectReason("");
  };

  const handleRejectReasonChange = (reason: string) => {
    setRejectReason(reason);
    if (reason !== "Other") {
      setCustomRejectReason("");
    }
  };

  const handleRejectSubmit = async () => {
    const finalReason = rejectReason === "Other" ? customRejectReason : rejectReason;
    if (!finalReason.trim()) {
      alert("Please provide a rejection reason.");
      return;
    }
    if (!rejectingSubmission) return;

    try {
      await updateDoc(doc(db, "submissions", rejectingSubmission.id), {
        status: 'rejected',
        rejectReason: finalReason,
        rejectedAt: Date.now(),
        rejectedBy: 'admin',
        adminReviewedAt: Date.now(),
        adminReviewedBy: 'admin'
      });

      const updatedSubmissions = submissions.filter(p => p.id !== rejectingSubmission.id);
      setSubmissions(updatedSubmissions);
      setSelected(updatedSubmissions.length > 0 ? updatedSubmissions[0] : null);
      setShowRejectModal(false);
      setRejectReason("");
      setCustomRejectReason("");
      setRejectingSubmission(null);
    } catch (error) {
      console.error("Error rejecting submission:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleRejectModalClose = () => {
    setShowRejectModal(false);
    setRejectReason("");
    setCustomRejectReason("");
    setRejectingSubmission(null);
  };

  const handleQAClick = () => {
    setQaReviewingSubmission(selected);
    setShowQAModal(true);
    setQaReason("");
  };

  const handleQASubmit = async () => {
    if (!qaReason.trim()) {
      alert("Please select a QA Review reason.");
      return;
    }
    if (!qaReviewingSubmission) return;

    try {
      await updateDoc(doc(db, "submissions", qaReviewingSubmission.id), {
        qaReviewStatus: 'pending',
        qaReviewReason: qaReason,
        qaReviewSentAt: Date.now(),
        qaReviewSentBy: 'admin'
      });

      const updatedSubmissions = submissions.filter(p => p.id !== qaReviewingSubmission.id);
      setSubmissions(updatedSubmissions);
      setSelected(updatedSubmissions.length > 0 ? updatedSubmissions[0] : null);

      setShowQAModal(false);
      setQaReason("");
      setQaReviewingSubmission(null);

      alert('Submission sent to QA Review!');
    } catch (error) {
      console.error("Error sending to QA Review:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleQAModalClose = () => {
    setShowQAModal(false);
    setQaReason("");
    setQaReviewingSubmission(null);
  };

  return (
    <div>
      <div className="border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold mb-4">Video Trimming</h1>
      </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2 border p-4 rounded">
            {loading ? (
              <div>Loading...</div>
            ) : selected ? (
              <div>
                <div className="mb-2">
                  <div className="flex items-center justify-between text-xl">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 font-bold">Task:</span>
                      <span className="text-gray-900">{selected.task?.title || '-'}</span>
                    </div>
                  </div>
                </div>
                <div className="mb-2 flex items-center gap-6 text-base">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Category:</span>
                    <span className="text-gray-900">{selected.task?.category || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Subcategory:</span>
                    <span className="text-gray-900">{selected.task?.subcategory || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Item Type:</span>
                    <span className="text-gray-900">{selected?.itemType || '-'}</span>
                  </div>
                </div>

                {selected.videoMetadata && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Video Source Metadata</div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm">
                        <span className={selected.videoMetadata.isMetaGlasses ? 'text-green-600' : 'text-red-500'}>
                          {selected.videoMetadata.isMetaGlasses ? '✅' : '❌'}
                        </span>
                        <span className="text-gray-600">
                          Device: {selected.videoMetadata.isMetaGlasses
                            ? 'Ray-Ban Meta Smart Glasses'
                            : (selected.videoMetadata.deviceModel || 'Unknown')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className={selected.videoMetadata.isMetaGlasses ? 'text-green-600' : 'text-red-500'}>
                          {selected.videoMetadata.isMetaGlasses ? '✅' : '❌'}
                        </span>
                        <span className="text-gray-600">
                          Copyright: {selected.videoMetadata.isMetaGlasses
                            ? 'Meta AI'
                            : (selected.videoMetadata.copyright || 'Unknown')}
                        </span>
                      </div>
                      {selected.videoMetadata.width > 0 && selected.videoMetadata.height > 0 && (
                        <div className="flex items-center space-x-2 text-sm">
                          <span className={selected.videoMetadata.isMetaGlasses ? 'text-green-600' : 'text-red-500'}>
                            {selected.videoMetadata.isMetaGlasses ? '✅' : '❌'}
                          </span>
                          <span className="text-gray-600">
                            Dimensions: {selected.videoMetadata.width}×{selected.videoMetadata.height}
                          </span>
                        </div>
                      )}
                      {selected.videoMetadata.duration > 0 && (
                        <div className="text-sm text-gray-600">
                          ⏱ Duration: {selected.videoMetadata.duration.toFixed(1)}s
                        </div>
                      )}
                      {selected.videoMetadata.fileSize > 0 && (
                        <div className="text-sm text-gray-600">
                          💾 File size: {(selected.videoMetadata.fileSize / (1024 * 1024)).toFixed(1)} MB
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!selected.videoMetadata && (
                  <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="text-sm text-yellow-700">
                      ⚠️ No video source metadata available for this submission
                    </div>
                  </div>
                )}

                {getSimilarityInfo(selected)}

                <div className="mb-4">
                  {currentImageUrl ? (
                    <>
                      <div className="text-sm text-gray-600 mb-1">
                        Video Submission
                      </div>
                      <div className="mt-1 w-full rounded border bg-black" style={{ maxHeight: 500 }}>
                        <video
                          src={currentImageUrl}
                          controls
                          className="w-full"
                          style={{ maxHeight: 500 }}
                          onLoadedMetadata={(e) => {
                            const video = e.currentTarget;
                            setVideoDuration(video.duration);
                            if (trimEndSec === 10 && video.duration > 10) {
                              setTrimEndSec(Math.min(video.duration, 30));
                            }
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center mt-2 p-8 bg-gray-50 rounded border">
                      <img
                        src="/placeholder.png"
                        alt="No submission"
                        className="max-w-xs rounded border opacity-50"
                        style={{ maxHeight: 150 }}
                      />
                      <span className="text-gray-500 text-sm mt-2">
                        No video available
                      </span>
                    </div>
                  )}
                </div>

                {selected && currentImageUrl && videoDuration > 0 && (
                  <div className="mb-4 p-4 bg-gray-50 rounded border">
                    <h4 className="font-semibold mb-3">Trim Video</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Time (seconds)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={videoDuration}
                          step="0.1"
                          value={trimStartSec}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setTrimStartSec(Math.max(0, Math.min(val, videoDuration)));
                          }}
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Range: 0 - {videoDuration.toFixed(1)}s
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Time (seconds)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max={videoDuration}
                          step="0.1"
                          value={trimEndSec}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value) || 0;
                            setTrimEndSec(Math.max(0, Math.min(val, videoDuration)));
                          }}
                          className="w-full p-2 border border-gray-300 rounded"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Range: 0 - {videoDuration.toFixed(1)}s
                        </div>
                      </div>
                      <div className="p-2 bg-blue-50 rounded">
                        <div className="text-sm">
                          <strong>Duration:</strong> {(trimEndSec - trimStartSec).toFixed(1)}s
                        </div>
                        {trimStartSec >= trimEndSec && (
                          <div className="text-xs text-red-600 mt-1">
                            ⚠️ End time must be greater than start time
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>No submission selected.</div>
            )}
          </div>

          <div className="lg:col-span-1 flex flex-col overflow-hidden">
            <div className="border p-4 rounded flex-shrink-0">
              {selected?.qaReviewReason && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                  <h4 className="font-semibold text-sm text-orange-900 mb-1">Sent to QA Reason</h4>
                  <p className="text-sm text-orange-800">{selected.qaReviewReason}</p>
                </div>
              )}

              <h3 className="font-semibold mb-3 text-lg">Validation Prompt</h3>
              <div className="text-base text-gray-900 min-h-[48px] whitespace-pre-wrap">
                {selected?.validationDetails?.prompt || selected?.validationPrompt || selected?.task?.validationPrompt || "-"}
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button
                  className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
                  onClick={handleRejectClick}
                  title="Reject video submission"
                >
                  Reject
                </button>
                <button
                  className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700"
                  onClick={handleQAClick}
                  title="Send to QA Review"
                >
                  Send to QA
                </button>
                <button
                  className={`px-4 py-2 rounded ${
                    trimming || trimStartSec >= trimEndSec || trimStartSec < 0 || trimEndSec > videoDuration
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
                  onClick={async () => {
                    if (!selected || trimming || trimStartSec >= trimEndSec || trimStartSec < 0 || trimEndSec > videoDuration) return;
                    setTrimming(true);
                    try {
                      await updateDoc(doc(db, "submissions", selected.id), {
                        videoStage: null,
                        trimmedVideoUrl: selected.mediaUrl || selected.videoUrl || '',
                        approvedAt: Date.now()
                      });
                      const updatedSubmissions = submissions.filter(p => p.id !== selected.id);
                      setSubmissions(updatedSubmissions);
                      setSelected(updatedSubmissions.length > 0 ? updatedSubmissions[0] : null);
                      alert('Video finalized!');
                    } catch (error: any) {
                      console.error("Error trimming video:", error);
                      alert(`Error trimming video: ${error?.message || 'Unknown error'}`);
                    } finally {
                      setTrimming(false);
                    }
                  }}
                  disabled={trimming || trimStartSec >= trimEndSec || trimStartSec < 0 || trimEndSec > videoDuration}
                  title={
                    trimming
                      ? 'Trimming in progress...'
                      : trimStartSec >= trimEndSec
                        ? 'End time must be greater than start time'
                        : 'Trim video and finalize'
                  }
                >
                  {trimming ? 'Trimming...' : 'Trim & Finalize'}
                </button>
              </div>
            </div>

            <div className="border p-4 rounded flex-1 flex flex-col mt-4 overflow-hidden" style={{ minHeight: 0, maxHeight: '100%' }}>
              <h3 className="font-semibold mb-2 flex-shrink-0">Review Queue ({submissions.length})</h3>
              <ul className="overflow-y-auto flex-1 pr-2" style={{ minHeight: 0 }}>
                {submissions.map(p => (
                  <li
                    key={p.id}
                    className={`p-2 mb-2 rounded cursor-pointer border ${selected?.id === p.id ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"}`}
                    onClick={() => setSelected(p)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-1">
                          {p.task?.title || "Unknown"}
                        </div>
                        <div className="text-sm text-gray-600">Category: {p.task?.category || "-"}</div>
                      </div>
                      {getStatusBadge(p.status)}
                    </div>
                    {p.validationDetails?.similarity && (
                      <div className="text-xs text-gray-500 mt-1">
                        Score: {(p.validationDetails.similarity * 100).toFixed(1)}%
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Reject Submission</h2>
              <button onClick={handleRejectModalClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>

            {rejectingSubmission && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  <strong>Task:</strong> {rejectingSubmission.task?.title || "Unknown"}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>User:</strong> {rejectingSubmission.userId}
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Rejection Reason:
              </label>
              <select
                value={rejectReason}
                onChange={(e) => handleRejectReasonChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Choose a reason...</option>
                {rejectionReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>

            {rejectReason === "Other" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Reason:
                </label>
                <textarea
                  value={customRejectReason}
                  onChange={(e) => setCustomRejectReason(e.target.value)}
                  placeholder="Please provide a specific reason for rejection..."
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <button
                onClick={handleRejectModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectReason || (rejectReason === "Other" && !customRejectReason.trim())}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Reject Submission
              </button>
            </div>
          </div>
        </div>
      )}

      {showQAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Send to QA Review</h2>
              <button onClick={handleQAModalClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>

            {qaReviewingSubmission && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  <strong>Task:</strong> {qaReviewingSubmission.task?.title || "Unknown"}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>User:</strong> {qaReviewingSubmission.userId}
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select QA Review Reason:
              </label>
              <select
                value={qaReason}
                onChange={(e) => setQaReason(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Choose a reason...</option>
                {qaReviewReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={handleQAModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleQASubmit}
                disabled={!qaReason.trim()}
                className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Send to QA Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
